const steps = [
  {
    title: "Pre-Filter",
    subtitle: "First line of defence",
    description: "Larger airborne particles are intercepted before they ever reach the core system — extending its lifespan and maintaining peak efficiency.",
    icon: "ri-filter-3-line",
    tag: "Stage 1",
    tagBg: "bg-amber-100",
    tagText: "text-amber-700",
    color: "bg-gradient-to-b from-amber-50 to-white",
    border: "border-amber-100",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    bullets: [
      { icon: "ri-check-line", text: "Traps dust, hair & pet dander" },
      { icon: "ri-check-line", text: "Protects Airdog TPA core from clogging" },
      { icon: "ri-check-line", text: "Washable & reusable" },
    ],
  },
  {
    title: "Airdog TPA Core",
    subtitle: "Where destruction happens",
    description: "High-voltage ionic wind generates an electrostatic field that doesn't trap pollutants — it obliterates them at the molecular level.",
    icon: "ri-flashlight-line",
    tag: "Stage 2",
    tagBg: "bg-brand/10",
    tagText: "text-brand",
    color: "bg-gradient-to-b from-brand/5 to-white",
    border: "border-brand/15",
    iconBg: "bg-brand/10",
    iconColor: "text-brand",
    bullets: [
      { icon: "ri-check-line", text: "Destroys particles down to 0.0146μm" },
      { icon: "ri-check-line", text: "Destroys viruses & bacteria" },
      { icon: "ri-check-line", text: "20× finer than HEPA filtration" },
    ],
  },
  {
    title: "Collecting Plates",
    subtitle: "Zero waste, zero cost",
    description: "Inactivated particles are magnetically collected on stainless steel plates. Simply rinse under water every few months — no replacement parts ever.",
    icon: "ri-stack-line",
    tag: "Stage 3",
    tagBg: "bg-emerald-100",
    tagText: "text-emerald-700",
    color: "bg-gradient-to-b from-emerald-50 to-white",
    border: "border-emerald-100",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    bullets: [
      { icon: "ri-check-line", text: "Reusable stainless steel plates" },
      { icon: "ri-check-line", text: "$0 ongoing filter costs" },
      { icon: "ri-check-line", text: "Rinse clean in under 5 minutes" },
    ],
  },
];

export default function TPATechSection() {
  return (
    <section id="how-it-works" className="min-h-[60vh] md:min-h-[70vh] flex flex-col justify-center py-6 md:py-8 bg-white scroll-mt-20 overflow-hidden relative">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">

        {/* Header row */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-5">
          <div>
            <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-brand mb-2">
              How It Works
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Airdog TPA Technology Explained
            </h2>
          </div>
          <p className="text-sm text-gray-700 font-semibold max-w-sm leading-relaxed lg:text-right">
            Two-Stage Plasma Air actively destroys airborne threats instead of just trapping them. No filters. No waste. Ever.
          </p>
        </div>

        {/* Step cards */}
        <div className="grid md:grid-cols-3 gap-3 mb-5">
          {steps.map((step, i) => (
            <div key={i} className="relative">
              <div className={`${step.color} ${step.border} border rounded-2xl p-4 h-full hover:-translate-y-1 transition-transform duration-300 flex flex-col`}>

                {/* Top: icon + tag */}
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl ${step.iconBg} flex items-center justify-center shrink-0`}>
                    <i className={`${step.icon} ${step.iconColor} text-lg`} />
                  </div>
                  <span className={`text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full ${step.tagBg} ${step.tagText}`}>
                    {step.tag}
                  </span>
                </div>

                {/* Title + subtitle */}
                <p className="text-[10px] font-bold tracking-wider text-gray-500 uppercase mb-0.5">{step.subtitle}</p>
                <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>

                {/* Description */}
                <p className="text-xs text-gray-700 leading-relaxed font-bold mb-3">{step.description}</p>

                {/* Divider */}
                <div className="h-px bg-gray-100 mb-3 mt-auto" />

                {/* Bullet points */}
                <ul className="space-y-1.5">
                  {step.bullets.map((b, j) => (
                    <li key={j} className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full ${step.iconBg} flex items-center justify-center shrink-0`}>
                        <i className={`${b.icon} ${step.iconColor} text-[10px]`} />
                      </div>
                      <span className="text-xs text-gray-800 font-bold">{b.text}</span>
                    </li>
                  ))}
                </ul>

              </div>
            </div>
          ))}
        </div>

        {/* Patents badge */}
        <div>
          <div className="flex flex-col sm:flex-row items-center gap-5 bg-gray-50 border border-gray-100 rounded-2xl px-6 py-4">
            <div className="flex items-center gap-5 shrink-0 sm:border-r sm:border-gray-200 sm:pr-8">
              <div className="text-6xl font-black text-brand leading-none">150+</div>
              <div>
                <p className="text-sm font-bold text-gray-900">Patents &amp; Certifications</p>
                <p className="text-sm text-gray-600 font-semibold">Awarded Globally</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                <i className="ri-award-line text-brand text-xl" />
              </div>
              <p className="text-sm text-gray-800 font-semibold leading-relaxed">
                Backed by rigorous independent testing and recognized by regulatory bodies across three continents.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}